import { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MDButton from '@components/MDButton';
import { apiErrorMessage } from '@functions/api-error-message';
import { AUTH_FIELD_SX, AuthLayout } from '../sign-in/auth-layout';
import { PasswordService } from './password.service';
import { environment } from 'routes/environment.router';

/// A mesma regra do cadastro de usuário e do servidor.
const MIN_PASSWORD = 8;

type Stage = 'checking' | 'form' | 'invalid' | 'done';

/// Onde o link do e-mail cai: confere o link antes do formulário, senão a pessoa digita a senha à toa.
export const ResetPasswordScreen = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';

    const [stage, setStage] = useState<Stage>('checking');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            setStage('invalid');
            return;
        }

        new PasswordService()
            .validate(token)
            .then((res) => setStage(res.data.data?.valid ? 'form' : 'invalid'))
            .catch(() => setStage('invalid'));
    }, [token]);

    const send = async (e: React.FormEvent) => {
        e.preventDefault();

        if (sending) return;

        if (password !== confirmation) {
            setError(t('senha.confirmacaoDiferente'));
            return;
        }

        setError('');
        setSending(true);

        try {
            await new PasswordService().reset({ token, newPassword: password, newPasswordConfirmation: confirmation });
            setStage('done');
        } catch (err) {
            setError(apiErrorMessage(err));
        } finally {
            setSending(false);
        }
    };

    const link = (label: string, to: string) => (
        <Box sx={{ textAlign: 'center' }}>
            <Link
                component="button"
                type="button"
                onClick={() => navigate(to, { replace: true })}
                sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}
            >
                {label}
            </Link>
        </Box>
    );

    if (stage === 'checking') {
        return (
            <AuthLayout subtitle={t('senha.conferindoLink')}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                </Box>
            </AuthLayout>
        );
    }

    if (stage === 'invalid') {
        return (
            <AuthLayout subtitle={t('senha.linkInvalidoTitulo')}>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, textAlign: 'center' }}>
                    {t('senha.linkInvalidoAviso')}
                </Typography>
                {environment.features.passwordReset && link(t('senha.pedirNovoLink'), '/esqueci-senha')}
                {link(t('senha.voltarAoLogin'), '/')}
            </AuthLayout>
        );
    }

    if (stage === 'done') {
        return (
            <AuthLayout subtitle={t('senha.redefinidaTitulo')}>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, textAlign: 'center' }}>
                    {t('senha.redefinidaAviso')}
                </Typography>
                <MDButton variant="gradient" color="info" size="large" onClick={() => navigate('/', { replace: true })}>
                    {t('senha.irParaLogin')}
                </MDButton>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout subtitle={t('senha.redefinirAviso')} onSubmit={send}>
            {error && (
                <Alert severity="error" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="caption">{error}</Typography>
                </Alert>
            )}

            <TextField
                type="password"
                label={t('senha.novaSenha')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                inputProps={{ minLength: MIN_PASSWORD, maxLength: 128 }}
                helperText={t('auth.senhaMinima')}
                autoComplete="new-password"
                sx={AUTH_FIELD_SX}
            />

            <TextField
                type="password"
                label={t('senha.confirmarSenha')}
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                required
                inputProps={{ minLength: MIN_PASSWORD, maxLength: 128 }}
                autoComplete="new-password"
                sx={AUTH_FIELD_SX}
            />

            <MDButton type="submit" variant="gradient" color="info" size="large" disabled={sending}>
                {sending ? <CircularProgress size={18} color="inherit" /> : t('senha.redefinirBotao')}
            </MDButton>
        </AuthLayout>
    );
};
